import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  signOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword as firebaseUpdatePassword,
  User as FirebaseUser,
  ParsedToken,
  IdTokenResult,
} from 'firebase/auth';
import { Users, Users_Select_Column } from '@gql/base-types';
import { GetUserProfileDocument } from '../queries/getUserProfile.query.generated';
import { GetUserProfileUpdatedAtDocument } from '../queries/getUserProfileUpdatedAt.query.generated';
import { GetUserPermissionsDocument } from '../queries/getUserPermissions.query.generated';
import app from './firebase.config';
import { execGraphqlWithFetch } from '@gql/fetch-client';
import siteConfig from '@config';
import { setPermissions, clearPermissions } from '@store';

interface Claims extends ParsedToken {
  'https://hasura.io/jwt/claims': {
    'x-hasura-default-role'?: string;
    'x-hasura-allowed-customers'?: string;
    'x-hasura-allowed-payers'?: string;
    'x-hasura-payer-child-customers'?: string;
    'x-hasura-allowed-regions'?: string;
    'x-hasura-allowed-roles'?: string;
    'x-hasura-customer-id'?: string;
    'x-hasura-user-id'?: string;
    'x-hasura-user-email'?: string;
  };
}

//Claims after they have been converted into their useable types
interface ConvertedClaims {
  'x-hasura-default-role'?: string;
  'x-hasura-allowed-customers'?: number[];
  'x-hasura-allowed-payers'?: number[];
  'x-hasura-payer-child-customers'?: number[];
  'x-hasura-allowed-regions'?: number[];
  'x-hasura-allowed-roles'?: string[];
  'x-hasura-customer-id'?: number;
  'x-hasura-user-id'?: number;
  'x-hasura-user-email'?: string;
}

export type User = {
  getToken: () => Promise<string | undefined>;
  profile: Users | null;
  claims: ConvertedClaims & { 'https://hasura.io/jwt/claims': Claims['https://hasura.io/jwt/claims'] };
};

const auth = getAuth(app);

export const buildConvenientGlobalUserObject = async (): Promise<User> => {
  const claims = await getClaimsFromToken();
  const userId = (claims as any)[siteConfig.firebaseClaimsUserId];
  const profile = await getUserProfile(userId); // This now also sets permissions
  
  const user: User = {
    getToken: getAccessTokenWithAutoRefresh,
    profile,
    claims: {
      ...claims,
      'https://hasura.io/jwt/claims': parsedToken?.claims[siteConfig.firebaseClaimsNamespace] || {},
    },
  };
  return user;
};

// Keep a reference to the parsed token for use in the buildConvenientGlobalUserObject function
let parsedToken: IdTokenResult | null = null;

export const getUserProfile = async (userId?: number): Promise<Users | null> => {
  try {
    const result = await execGraphqlWithFetch<any>({
      query: GetUserProfileDocument,
      variables: { userId },
    });

    if (result?.id) {
      if (result.users_usertofeatures) {
        const permissions = result.users_usertofeatures.map((item: any) => item.feature.name);
        setPermissions(permissions);
      } else {
        setPermissions([]);
      }

      return result as Users;
    }

    return null;
  } catch (err) {
    console.log('Error getting user', err);
    return null;
  }
};

export const getUserProfileLastUpdatedAt = async (userId: number): Promise<Users_Select_Column.UpdatedAt | null> => {
  try {
    const user = await execGraphqlWithFetch<Users>({
      query: GetUserProfileUpdatedAtDocument,
      variables: { userId },
    });

    if ((user as Users).updated_at) {
      return user?.updated_at as Users_Select_Column.UpdatedAt;
    }

    return null;
  } catch (err) {
    console.log('Error getting user', err);
    return null;
  }
};

export const getUserPermissions = async (userId?: number): Promise<string[]> => {
  try {
    if (!userId) {
      return [];
    }

    const result = await execGraphqlWithFetch<any>({
      query: GetUserPermissionsDocument,
      variables: { userId },
    });

    // The result is the user object directly, not wrapped in users_by_pk
    if (!result?.id) {
      return [];
    }

    // Check if user has usertofeatures array (even if empty)
    if (!result.users_usertofeatures) {
      return [];
    }

    // Extract permissions from the features
    const permissions = result.users_usertofeatures.map((item: any) => item.feature.name);
    return permissions;
  } catch (err) {
    console.log('Error getting user permissions', err);
    return []; // Fail restrictive - return empty array if error
  }
};

export const getClaimsFromToken = async (): Promise<ConvertedClaims> => {
  const tokenResult = await auth.currentUser?.getIdTokenResult();
  parsedToken = tokenResult || null;
  const rawClaims = JSON.parse(JSON.stringify(parsedToken?.claims[siteConfig.firebaseClaimsNamespace] || {}));

  if (rawClaims[siteConfig.firebaseClaimsAllowedRegions])
    rawClaims[siteConfig.firebaseClaimsAllowedRegions] = JSON.parse(
      getArrayFromHasuraArray(rawClaims[siteConfig.firebaseClaimsAllowedRegions])
    );

  if (rawClaims[siteConfig.firebaseClaimsPayerChildCustomers])
    rawClaims[siteConfig.firebaseClaimsPayerChildCustomers] = JSON.parse(
      getArrayFromHasuraArray(rawClaims[siteConfig.firebaseClaimsPayerChildCustomers])
    );

  if (rawClaims[siteConfig.firebaseClaimsAllowedCustomers])
    rawClaims[siteConfig.firebaseClaimsAllowedCustomers] = JSON.parse(
      getArrayFromHasuraArray(rawClaims[siteConfig.firebaseClaimsAllowedCustomers])
    );

  if (rawClaims[siteConfig.firebaseClaimsAllowedPayers])
    rawClaims[siteConfig.firebaseClaimsAllowedPayers] = JSON.parse(
      getArrayFromHasuraArray(rawClaims[siteConfig.firebaseClaimsAllowedPayers])
    );

  if (
    rawClaims[siteConfig.firebaseClaimsCustomerId] &&
    !isNaN(Number(rawClaims[siteConfig.firebaseClaimsCustomerId]))
  ) {
    rawClaims[siteConfig.firebaseClaimsCustomerId] = parseInt(rawClaims[siteConfig.firebaseClaimsCustomerId]);
  }

  return rawClaims;
};

// Sign-in gate: only `admin` default roles may use this app (see permittedRoles.ts).
export { checkForPermittedRole } from './permittedRoles';

export const checkForPowerRole = (user?: { claims?: ConvertedClaims }) => {
  const allowedRoles = user?.claims?.['x-hasura-allowed-roles'] || [];
  const defaultRole = user?.claims?.['x-hasura-default-role'] || ``;
  if (!allowedRoles.includes(defaultRole)) allowedRoles.push(defaultRole);
  return (
    allowedRoles.includes(`admin`) ||
    allowedRoles.includes(`dispatcher`) ||
    allowedRoles.includes(`dealer-super-admin`) ||
    allowedRoles.includes(`dealer-admin`)
  );
};

const getArrayFromHasuraArray = (arr: string) => {
  try {
    const replacedJSON = JSON.stringify(arr).replace('{', '[').replace('}', ']');
    const parsedJSON = JSON.parse(replacedJSON);
    return parsedJSON;
  } catch (error) {
    console.log(`Failed to convert Hasura array`, { hasuraArray: arr, error });
    return;
  }
};

const login = (email: string, password: string) => {
  //console.log('[authService] login', { email, password });
  return signInWithEmailAndPassword(auth, email, password);
};

const logout = () => {
  clearPermissions();
  return signOut(auth);
};

const resetPassword = (email: string) => {
  return sendPasswordResetEmail(auth, email);
};

const onAuthStateChanged = (callback: (user: FirebaseUser | null) => void) => {
  //console.log('[authService] onAuthStateChanged', { callback });
  return firebaseOnAuthStateChanged(auth, callback);
};

const getAccessTokenWithAutoRefresh = async (): Promise<string | undefined> => {
  try {
    let userId;
    let tokenIssued;
    let firebaseTokenResult = null;
    if (auth && auth.currentUser) {
      firebaseTokenResult = await auth.currentUser.getIdTokenResult();
    }
    return firebaseTokenResult?.token;
  } catch (err) {
    console.log('Error getting most up-to-date token', err);
    return;
  }
};

const getAuthStatus = async () => {
  if (auth && auth.currentUser) {
    return true;
  } else return false;
};

const getAccessToken = async () => {
  try {
    const token = await auth.currentUser?.getIdToken();
    return token;
  } catch (err) {
    console.log('Error getting token', err);
    return;
  }
};

const writeUserEvent = async (action: string) => {
  //console.log('user eventlog action', action);
  // const claims = await getClaimsFromToken();
  // const userId = claims['x-hasura-user-id'];
  // const email = claims['x-hasura-user-email'];
  // const token = await getAccessToken();
  //   try {
  //     const userEventlogRes =  await axios({
  //       method: `POST`,
  //       url: `/.netlify/functions/writeUserEventLogs`,
  //       data: {
  //         actorEmail: email,
  //         action: action,
  //         affectedUserId: userId,
  //       },
  //       headers: {
  //         'content-type': 'application/json',
  //         authorization: `Bearer ${token}`,
  //       },
  //     })

  //     console.log('userEventlogRes', userEventlogRes)

  //     if (userEventlogRes && userEventlogRes.status && userEventlogRes.status !== 200) {
  //       console.log('Failed to write user eventlog:', userEventlogRes);
  //     }
  //   } catch (err) {
  //     console.error(`Failed to write user eventlog:`, err);
  //   }
};

const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user || !user.email) throw new Error('No authenticated user');
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await firebaseUpdatePassword(user, newPassword);
};

const authService = {
  login,
  logout,
  resetPassword,
  changePassword,
  onAuthStateChanged,
  getAccessTokenWithAutoRefresh,
  getAccessToken,
  getAuthStatus,
  writeUserEvent,
};

export { auth, authService };

export default authService;
